import { useEffect, useState, type FormEvent, type JSX } from 'react';
import {
  LINK_SIDES,
  linksLosingPoint,
  MAX_LINK_POINTS,
  type Link,
  type LinkPoints,
  type LinkSide,
  type MapLayer,
  type NetworkNode,
  type NodeGroup,
  type NodeKind,
} from '@netdeck/contracts';
import { Button } from '../../shared/ui/Button';
import { Field } from '../../shared/ui/Field';
import type { TFunction } from 'i18next';
import { useTranslation } from '../../shared/i18n';
import { useErrorText } from '../../shared/i18n/useErrorText';
import { NODE_KIND_LABEL_KEYS } from '../../shared/ui/NodeIcon';

/** Произвольное поле метаданных: инвентарный номер, MAC, версия ОС. */
interface MetadataRow {
  key: string;
  value: string;
  /** Показывать ли поле на карточке узла в карте. */
  pinned: boolean;
}

/**
 * Сколько полей разрешено вывести на карточку. Столько же стоит в контракте:
 * карточка узка, и пятая строка вытеснит с неё имя и адрес — то, ради чего
 * на карту смотрят.
 */
const PINNED_LIMIT = 4;

export interface NodePropertiesProps {
  node: NetworkNode;
  groups: NodeGroup[];
  /**
   * Слои площадки. Не переданы — строки слоя в панели нет: на экране узлов
   * карты не видно, и переносить узел там некуда.
   */
  layers?: readonly MapLayer[];
  /**
   * Слои, погашенные зрителем. Нужны затем, чтобы сказать это вслух:
   * узел, переехавший на скрытый слой, пропадает с экрана, и без пометки
   * это выглядит как потеря узла.
   */
  hiddenLayers?: ReadonlySet<string>;
  /**
   * Связи площадки — только для предупреждения о потерянных точках.
   *
   * Не переданы — предупредить не о чем и нечем: на экране узлов карты нет,
   * и точки там не настраивают.
   */
  links?: readonly Link[];
  canWrite: boolean;
  pending: boolean;
  error: Error | null;
  /**
   * Сохранение. Обещание обязано отклониться при отказе сервера: по нему
   * форма понимает, закрываться ей или остаться с введённым текстом.
   */
  onSave: (input: {
    name: string;
    address: string | null;
    kind: NodeKind;
    groupId: string | null;
    layerId?: string;
    notes: string | null;
    metadata: Record<string, unknown>;
    pinnedMetadata: string[];
    linkPoints: LinkPoints;
  }) => Promise<unknown>;
}

/**
 * Свойства узла — панель из раздела 03 макета: имя, адрес, тип, группа,
 * заметки и произвольные поля метаданных.
 *
 * Просмотр и правка разведены явной кнопкой, а не редактированием по месту.
 * Инженер держит этот экран открытым во время работ, и поле, которое меняется
 * от случайного клика, здесь опаснее лишнего нажатия: адрес узла — то, куда
 * платформа потом пойдёт подключаться.
 */
export function NodeProperties({
  node,
  groups,
  layers,
  hiddenLayers,
  links,
  canWrite,
  pending,
  error,
  onSave,
}: NodePropertiesProps): JSX.Element {
  const { t } = useTranslation();

  const [editing, setEditing] = useState(false);

  // Правка закрывается при переходе к другому узлу: иначе форма осталась бы
  // открытой с полями предыдущего.
  useEffect(() => setEditing(false), [node.id]);

  if (!editing) {
    return (
      <section className="flex flex-col gap-2.5 border-b border-line px-3.5 py-3">
        <dl className="grid grid-cols-[86px_1fr] items-center gap-x-3 gap-y-1.5 text-sm">
          <PropertyRow label={t('nodes.field.name')} value={node.name} />
          <PropertyRow
            label={t('nodes.field.address')}
            value={node.address ?? t('common.dash')}
            mono
          />
          <PropertyRow label={t('nodes.field.kind')} value={t(NODE_KIND_LABEL_KEYS[node.kind])} />
          <PropertyRow
            label={t('nodes.field.group')}
            value={
              groups.find((group) => group.id === node.groupId)?.name ?? t('nodes.field.noGroup')
            }
          />
          {layers ? (
            <PropertyRow
              label={t('nodes.field.layer')}
              value={layers.find((layer) => layer.id === node.layerId)?.name ?? t('common.dash')}
            />
          ) : null}
        </dl>

        {node.notes ? (
          <p className="text-sm leading-[1.45] whitespace-pre-wrap text-muted">{node.notes}</p>
        ) : null}

        <MetadataView metadata={node.metadata} pinned={node.pinnedMetadata} />

        {canWrite ? (
          <div>
            <Button icon="pencil" size="sm" onClick={() => setEditing(true)}>
              {t('monitors.edit')}
            </Button>
          </div>
        ) : null}
      </section>
    );
  }

  return (
    <NodeForm
      node={node}
      groups={groups}
      layers={layers}
      hiddenLayers={hiddenLayers}
      links={links}
      pending={pending}
      error={error}
      onCancel={() => setEditing(false)}
      onSave={async (input) => {
        try {
          await onSave(input);
          setEditing(false);
        } catch {
          // Форма остаётся открытой с введённым текстом: закрыть её после
          // отказа значит потерять правку и не сказать об этом. Сама ошибка
          // приходит через `error` и показывается рядом с кнопкой.
        }
      }}
    />
  );
}

function PropertyRow({
  label,
  value,
  mono,
}: {
  label: string;
  value: string;
  mono?: boolean;
}): JSX.Element {
  return (
    <>
      <dt className="font-mono text-xs text-dim">{label}</dt>
      <dd className={`truncate ${mono ? 'font-mono text-muted' : ''}`} title={value}>
        {value}
      </dd>
    </>
  );
}

function MetadataView({
  metadata,
  pinned,
}: {
  metadata: Record<string, unknown>;
  pinned: string[];
}): JSX.Element | null {
  const { t } = useTranslation();

  const rows = Object.entries(metadata);
  if (rows.length === 0) {
    return null;
  }

  return (
    <div className="flex flex-col gap-1.5">
      <div className="font-mono text-2xs tracking-[0.06em] text-faint uppercase">
        {t('nodes.metadata.title')}
      </div>
      <dl className="grid grid-cols-[86px_1fr] gap-x-3 gap-y-1 font-mono text-xs">
        {rows.map(([key, value]) => (
          <div key={key} className="contents">
            <dt className="truncate text-dim" title={key}>
              {key}
            </dt>
            <dd className="flex min-w-0 items-baseline gap-2 text-muted">
              <span className="truncate" title={String(value)}>
                {String(value)}
              </span>
              {pinned.includes(key) ? (
                <span className="flex-none text-2xs text-faint">{t('nodes.metadata.onMap')}</span>
              ) : null}
            </dd>
          </div>
        ))}
      </dl>
    </div>
  );
}

function NodeForm({
  node,
  groups,
  layers,
  hiddenLayers,
  pending,
  error,
  links,
  onCancel,
  onSave,
}: Omit<NodePropertiesProps, 'canWrite'> & { onCancel: () => void }): JSX.Element {
  const { t } = useTranslation();
  const errorText = useErrorText();

  const [name, setName] = useState(node.name);
  const [address, setAddress] = useState(node.address ?? '');
  const [kind, setKind] = useState<NodeKind>(node.kind);
  const [groupId, setGroupId] = useState(node.groupId ?? '');
  const [layerId, setLayerId] = useState(node.layerId);
  const [notes, setNotes] = useState(node.notes ?? '');
  const [metadata, setMetadata] = useState<MetadataRow[]>(
    toRows(node.metadata, node.pinnedMetadata),
  );
  const [linkPoints, setLinkPoints] = useState<LinkPoints>(node.linkPoints);

  /*
    Связи, которые не переживут правку: их точек крепления после неё
    не станет. Считается тем же правилом, что и на сервере, — иначе форма
    предупредит про две связи, а удалено будет три.
  */
  const losing = linksLosingPoint(node.id, linkPoints, links ?? []);

  /*
    Узел в рамке лежит на слое этой рамки — так требует сервер, и иначе узел
    пропал бы вместе с рамкой, которая на его слое не рисуется. Поэтому слой
    здесь не выбирают, а показывают: он следует за выбранной рамкой.
  */
  const frameLayerId =
    groupId === '' ? null : (groups.find((group) => group.id === groupId)?.layerId ?? null);
  const effectiveLayerId = frameLayerId ?? layerId;

  function submit(event: FormEvent): void {
    event.preventDefault();

    /*
      Спрашиваем до сохранения, а не сообщаем после: связь удаляется
      без возврата, и «мы тут заодно стёрли три ваши линии» — не то,
      что человек должен узнавать по факту. Число называется вслух:
      согласие на «какие-то связи» согласием не является.
    */
    if (losing.size > 0 && !confirm(losingQuestion(losing.size, t))) {
      return;
    }

    void onSave({
      name: name.trim(),
      address: address.trim() || null,
      kind,
      groupId: groupId || null,
      // Слой отправляется только там, где его показывали: на экране узлов
      // выбора нет, и посылать оттуда прежнее значение незачем.
      ...(layers ? { layerId: effectiveLayerId } : {}),
      notes: notes.trim() || null,
      metadata: fromRows(metadata),
      pinnedMetadata: pinnedKeys(metadata),
      linkPoints,
    });
  }

  return (
    <form
      onSubmit={submit}
      className="flex flex-col gap-3 border-b border-line bg-row-alt px-3.5 py-3"
    >
      <Field
        label={t('nodes.field.name')}
        required
        value={name}
        onChange={(event) => setName(event.target.value)}
      />

      <Field
        label={t('nodes.field.address')}
        mono
        value={address}
        onChange={(event) => setAddress(event.target.value)}
        placeholder="10.20.0.11"
        hint={t('nodes.form.addressHint')}
      />

      <label className="flex flex-col gap-1.5">
        <span className="text-sm text-muted">{t('nodes.field.kind')}</span>
        <select
          value={kind}
          onChange={(event) => setKind(event.target.value as NodeKind)}
          className="h-[34px] rounded-sm border border-line-strong bg-raised px-2 text-base text-fg"
        >
          {Object.entries(NODE_KIND_LABEL_KEYS).map(([value, key]) => (
            <option key={value} value={value}>
              {t(key)}
            </option>
          ))}
        </select>
      </label>

      <label className="flex flex-col gap-1.5">
        <span className="text-sm text-muted">{t('nodes.field.group')}</span>
        <select
          value={groupId}
          onChange={(event) => setGroupId(event.target.value)}
          className="h-[34px] rounded-sm border border-line-strong bg-raised px-2 text-base text-fg"
        >
          <option value="">{t('nodes.field.noGroup')}</option>
          {groups.map((group) => (
            <option key={group.id} value={group.id}>
              {group.name}
            </option>
          ))}
        </select>
      </label>

      {layers ? (
        <label className="flex flex-col gap-1.5">
          <span className="text-sm text-muted">{t('nodes.field.layer')}</span>
          <select
            value={effectiveLayerId}
            disabled={frameLayerId !== null}
            onChange={(event) => setLayerId(event.target.value)}
            aria-label={t('nodes.form.layerAria')}
            title={
              frameLayerId === null ? t('nodes.form.layerHint') : t('nodes.form.layerFromFrame')
            }
            className="h-[34px] rounded-sm border border-line-strong bg-raised px-2 text-base text-fg disabled:opacity-50"
          >
            {layers.map((layer) => (
              <option key={layer.id} value={layer.id}>
                {hiddenLayers?.has(layer.id)
                  ? t('nodes.form.layerHidden', { name: layer.name })
                  : layer.name}
              </option>
            ))}
          </select>
          {frameLayerId === null ? null : (
            <span className="text-xs text-faint">{t('nodes.form.movesWithFrame')}</span>
          )}
          {hiddenLayers?.has(effectiveLayerId) ? (
            <span className="text-xs text-warn">{t('nodes.form.layerIsHidden')}</span>
          ) : null}
        </label>
      ) : null}

      {links === undefined ? null : (
        <fieldset className="flex flex-col gap-1.5">
          <legend className="text-sm text-muted">{t('nodes.linkPoints.title')}</legend>

          <p className="text-xs text-dim">{t('nodes.linkPoints.hint')}</p>

          <div className="grid grid-cols-4 gap-2">
            {LINK_SIDES.map((side) => (
              <label key={side} className="flex flex-col gap-1">
                <span className="text-xs text-dim">{t(SIDE_LABEL_KEYS[side])}</span>
                <input
                  type="number"
                  min={1}
                  max={MAX_LINK_POINTS}
                  value={linkPoints[side]}
                  aria-label={t('nodes.linkPoints.sideAria', {
                    side: t(SIDE_LABEL_KEYS[side]),
                  })}
                  onChange={(event) =>
                    setLinkPoints((current) => ({
                      ...current,
                      [side]: clampPoints(event.target.value, current[side]),
                    }))
                  }
                  className="h-8 rounded-sm border border-line-strong bg-raised px-2 font-mono text-sm text-fg"
                />
              </label>
            ))}
          </div>

          {/*
            Предупреждение стоит рядом с полями, а не только в окне
            подтверждения: увидеть цену правки надо до того, как потянулся
            к «Сохранить», иначе окно приходит неожиданностью.
          */}
          {losing.size === 0 ? null : (
            <p className="text-xs text-warn">{losingWarning(losing.size, t)}</p>
          )}
        </fieldset>
      )}

      <label className="flex flex-col gap-1.5">
        <span className="text-sm text-muted">{t('nodes.field.notes')}</span>
        <textarea
          value={notes}
          onChange={(event) => setNotes(event.target.value)}
          rows={3}
          placeholder={t('nodes.form.notesPlaceholder')}
          className="rounded-sm border border-line-strong bg-raised px-2 py-1.5 text-base text-fg placeholder:text-faint"
        />
      </label>

      <MetadataEditor rows={metadata} onChange={setMetadata} />

      <div className="flex items-center gap-2">
        <Button
          icon="check"
          type="submit"
          variant="primary"
          size="sm"
          disabled={pending || !name.trim()}
        >
          {pending ? t('sites.form.saving') : t('common.action.save')}
        </Button>
        <Button icon="close" size="sm" onClick={onCancel} disabled={pending}>
          {t('common.action.cancel')}
        </Button>
        {error ? <span className="text-sm text-down-fg">{errorText(error)}</span> : null}
      </div>
    </form>
  );
}

/**
 * Редактор произвольных полей. Пары ключ-значение, без типов: §7.4 отводит
 * под них JSONB именно затем, чтобы заказчик держал там своё — инвентарный
 * номер, MAC, версию ОС, — не спрашивая разработчика про новую колонку.
 *
 * Флажок «на карте» у каждой строки по умолчанию снят: полей бывает
 * два десятка, и вывести их все — значит превратить карту в таблицу.
 * Что видно на карте, решает тот, кто заполняет свойства.
 */
function MetadataEditor({
  rows,
  onChange,
}: {
  rows: MetadataRow[];
  onChange: (rows: MetadataRow[]) => void;
}): JSX.Element {
  const { t } = useTranslation();

  function update(index: number, patch: Partial<MetadataRow>): void {
    onChange(rows.map((row, i) => (i === index ? { ...row, ...patch } : row)));
  }

  const pinnedCount = rows.filter((row) => row.pinned && row.key.trim()).length;

  return (
    <div className="flex flex-col gap-1.5">
      <span className="text-sm text-muted">{t('nodes.metadata.title')}</span>

      {rows.map((row, index) => (
        <div key={index} className="flex items-center gap-2">
          <input
            value={row.key}
            onChange={(event) => update(index, { key: event.target.value })}
            placeholder={t('nodes.metadata.key')}
            aria-label={t('nodes.metadata.keyAria', { index: index + 1 })}
            className="h-[30px] w-[110px] rounded-sm border border-line-strong bg-raised px-2 font-mono text-sm text-fg placeholder:text-faint"
          />
          <input
            value={row.value}
            onChange={(event) => update(index, { value: event.target.value })}
            placeholder={t('nodes.metadata.value')}
            aria-label={t('nodes.metadata.valueAria', { index: index + 1 })}
            className="h-[30px] min-w-0 flex-1 rounded-sm border border-line-strong bg-raised px-2 font-mono text-sm text-fg placeholder:text-faint"
          />
          {/*
            Единственная кнопка без подписи: в ряду полей на неё нет места.
            Поэтому имя задано явно — иконка сама по себе для скринридера
            невидима, и кнопка без него осталась бы безымянной.
          */}
          {/*
            Флажок без видимой подписи — на неё в ряду полей нет места,
            поэтому имя задано явно, как и у кнопки удаления.
          */}
          <input
            type="checkbox"
            checked={row.pinned}
            disabled={!row.pinned && pinnedCount >= PINNED_LIMIT}
            onChange={(event) => update(index, { pinned: event.target.checked })}
            title={t('nodes.metadata.pinAria', { index: index + 1 })}
            aria-label={t('nodes.metadata.pinAria', { index: index + 1 })}
            className="flex-none"
          />
          <Button
            icon="trash"
            size="sm"
            variant="danger"
            onClick={() => onChange(rows.filter((_, i) => i !== index))}
            title={t('nodes.metadata.deleteAria', { index: index + 1 })}
            aria-label={t('nodes.metadata.deleteAria', { index: index + 1 })}
          />
        </div>
      ))}

      <div className="flex items-center gap-2">
        <Button
          icon="plus"
          size="sm"
          onClick={() => onChange([...rows, { key: '', value: '', pinned: false }])}
        >
          {t('nodes.metadata.add')}
        </Button>
        <span className="text-xs text-faint">
          {t('nodes.metadata.pinHint', { limit: PINNED_LIMIT })}
        </span>
      </div>
    </div>
  );
}

function toRows(metadata: Record<string, unknown>, pinned: string[]): MetadataRow[] {
  return Object.entries(metadata).map(([key, value]) => ({
    key,
    value: String(value),
    pinned: pinned.includes(key),
  }));
}

/**
 * Отмеченные поля — в том же порядке, в каком они идут в форме: карточка
 * узла выводит их подряд, и порядок задаёт тот, кто заполняет свойства.
 * Поля без имени отброшены по той же причине, что и в `fromRows`.
 */
function pinnedKeys(rows: MetadataRow[]): string[] {
  const keys: string[] = [];

  for (const row of rows) {
    const key = row.key.trim();
    if (row.pinned && key && !keys.includes(key)) {
      keys.push(key);
    }
  }

  return keys.slice(0, PINNED_LIMIT);
}

/**
 * Поля без имени отбрасываются, а не сохраняются пустым ключом: строка,
 * которую начали заполнять и бросили, не должна превращаться в запись `"": ""`.
 */
function fromRows(rows: MetadataRow[]): Record<string, unknown> {
  const result: Record<string, unknown> = {};

  for (const row of rows) {
    const key = row.key.trim();
    if (key) {
      result[key] = row.value;
    }
  }

  return result;
}

/** Стороны узла — ключами словаря. Порядок тот же, что в контрактах: сверху по часовой. */
const SIDE_LABEL_KEYS: Readonly<Record<LinkSide, string>> = {
  top: 'nodes.side.top',
  right: 'nodes.side.right',
  bottom: 'nodes.side.bottom',
  left: 'nodes.side.left',
};

/**
 * Число точек из того, что набрали в поле.
 *
 * Пустое поле и мусор возвращают прежнее значение, а не единицу: человек,
 * стирающий «3», чтобы набрать «4», на мгновение оставляет поле пустым —
 * и обнулять из-за этого сторону значило бы удалить его связи посреди
 * набора числа.
 */
function clampPoints(raw: string, previous: number): number {
  const value = Number.parseInt(raw, 10);

  if (!Number.isInteger(value)) {
    return previous;
  }

  return Math.min(Math.max(value, 1), MAX_LINK_POINTS);
}

/** Предупреждение рядом с полями — до того, как потянулись к «Сохранить». */
export function losingWarning(count: number, t: TFunction): string {
  return t('nodes.linkPoints.warning', { links: t('nodes.linksCount', { count }) });
}

/** Вопрос перед сохранением. Число называется вслух: «какие-то связи» — не согласие. */
export function losingQuestion(count: number, t: TFunction): string {
  return t('nodes.linkPoints.question', { links: t('nodes.linksCount', { count }) });
}
